/**
 * @file        src/modals/FlightLoadingModal.jsx
 * @description Transient "loading flights" overlay. Premium dark/neon card with
 *              the Neon Holidays wordmark, a route (origin ✈ destination), a
 *              0–100% progress runway the plane advances along, soft neon glow,
 *              animated dots and a rotating reassurance line. Route / dates / pax
 *              reflect the real search. Shown while the /air-search query is in
 *              flight.
 *
 *              THE PERCENTAGE IS MODELLED, NOT MEASURED — see the PROGRESS MODEL
 *              block below before changing it. Its one hard rule: the timer may
 *              never reach 100%, because only a finished search means finished.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-09-12 Claude — Percentage readout MOVED: it was right-aligned above the
 *                          runway, hanging over the end of the track, where it read
 *                          as a label on the finish line rather than on the search.
 *                          It now sits CENTRED between the runway and the bouncing
 *                          dots, a size larger. The clearance from the dots is
 *                          explicit and not interchangeable with the old spacing —
 *                          the dots bounce upward into whatever sits above them.
 *   - 2026-09-10 Claude — Plane no longer bobs: the `nhBob` up-and-down keyframe is
 *                          removed, so it tracks the runway in one straight line.
 *                          A vertical wobble on something that is also a progress
 *                          indicator made its position read as imprecise. Nothing
 *                          else changed — same plane, same glow, same track.
 *   - 2026-09-10 Claude — Loader did not appear AT ALL on one-way / round trip, from
 *                          the phase refactor below: `phase` was seeded 'idle' and
 *                          only left it on an open false→true transition, but the
 *                          results page mounts this with its query already fetching,
 *                          so on those trip types the first render is open=true and
 *                          there is no transition to catch (prevOpen seeds from the
 *                          same prop). Multi-city masked it — its query enables a
 *                          render later, so open genuinely does flip there. `phase`
 *                          now seeds from `open` like `prevOpen` does.
 *   - 2026-09-10 Claude — "Bar hits 80%, results appear, THEN the last 20% plays."
 *                          The card was genuinely LEAVING THE DOM and coming back:
 *                          visibility was `open || outro` with `outro` set by an
 *                          effect, and effects run after the render commits — so
 *                          the frame where `open` went false returned null, painted
 *                          the results, and only then did the effect bring the card
 *                          back to finish. Visibility is now derived DURING render
 *                          via a 3-state phase (idle → running → finishing), set
 *                          with React's documented adjust-state-on-prop-change
 *                          pattern, so that null render is discarded before it is
 *                          ever painted. The body-scroll lock moved onto `phase`
 *                          for the same reason, and the backdrop went /85 → /95
 *                          since the results do render behind it during the finish.
 *   - 2026-09-10 Claude — Two restart bugs on a MODIFIED search. (1) The bar ran
 *                          BACKWARDS: resetting to 0 on the existing node let the
 *                          CSS transition animate width 100%→0, sweeping the fill
 *                          right-to-left with the plane flying in reverse. The fill
 *                          and the plane now carry a per-run `key`, so a reset
 *                          REMOUNTS them and a fresh node simply renders at 0.
 *                          (2) A one-render flicker of `open` on the SAME search
 *                          tore the run down and restarted it, which read as
 *                          results flashing mid-search and the loader starting
 *                          over. The modal now takes a `runKey` (flightSearch.seq)
 *                          so it can tell a new search from a flicker: a new key
 *                          resets to 0, the same key resumes the climb from the
 *                          start time it kept in a ref. The ramp is also monotonic
 *                          now — it may stall, never retreat.
 *   - 2026-09-10 Claude — The FINISH is now watchable: the sweep from wherever the
 *                          search landed up to 100 went 240ms → 700ms, eased-out,
 *                          stepped every 25ms so the digits visibly count, and 100%
 *                          then holds 300ms. At 240ms the run home — a third of the
 *                          track from a typical ~70% — was over before the eye
 *                          caught it, so it still read as "70%, blink, results".
 *                          The width/left transitions dropped 200ms → 150ms so the
 *                          bar tracks the number instead of trailing it into the
 *                          hold. Total outro ≈ 1s.
 *   - 2026-09-10 Claude — Added a 0–100% progress readout, and made the runway
 *                          ITSELF the bar: the plane now sits AT the percentage
 *                          with the track filled behind it, instead of looping on a
 *                          timer that meant nothing. The number is an ESTIMATE and
 *                          is documented as one — /air-search is a single request
 *                          with no intermediate signal — so it eases out toward a
 *                          90% CEILING it can never pass on the timer alone, and
 *                          only the query actually resolving takes it to 100. That
 *                          finish SWEEPS (FINISH_MS) and then holds (HOLD_MS), so
 *                          the bar is seen to complete rather than vanishing
 *                          mid-climb or cutting to 100.
 *   - 2026-09-10 Claude — Progress curve exponential → QUADRATIC ease-out, and the
 *                          "SEARCHING" caption dropped (the rotating status line
 *                          under the bar already says it). The exponential shape ran
 *                          ~30%/s at the start and ~1%/s at the end, so it sprinted
 *                          to 50% in two seconds and then looked stalled for the
 *                          rest of the search; quadratic spans ~16%/s to ~2%/s and
 *                          reads as one continuous movement. Now derived from
 *                          ELAPSED TIME each tick rather than accumulated per-tick
 *                          increments, so a throttled tab cannot leave it behind.
 *   - 2026-07-31 Claude — Third bouncing dot was `bg-sky` (blue) against the two
 *                         green ones — now `bg-neon`, so the trio reads as one
 *                         brand-coloured loader.
 *   - 2026-06-22 Claude — Initial port. No backdrop click — the parent
 *                         flight-search flow closes this on its own timer,
 *                         but Esc still escapes for safety.
 *   - 2026-06-25 Claude — Accept from/to/when/pax props; shown live while the
 *                         /air-search query is in flight.
 *   - 2026-06-29 Claude — Redesigned: Neon wordmark, gliding-plane runway,
 *                         neon glow, rotating status line.
 *   - 2026-07-03 Claude — Route separator now a direction plane (was the ⇄
 *                         swap-arrows glyph, which read as a round-trip toggle).
 *   - 2026-07-06 Claude — Route separator reflects trip type: double arrow (⇌)
 *                         for round trip, single arrow (→) for one-way (via the
 *                         new `roundTrip` prop). Runway plane below unchanged.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import NeonWordmark from '../components/NeonWordmark.jsx';

const TIPS = [
  'Loading the best deals across 500+ airlines…',
  'Comparing fares, baggage and cancellation rules…',
  'Finding the fastest routes and lowest prices…',
  'Locking in live availability for your dates…',
];

// ---- PROGRESS MODEL -------------------------------------------------------
// /air-search is ONE request with no intermediate signal — it is pending, then it
// is done — and nearly all of the wait is the supplier's, not the download's. So
// there is no true percentage to read; this is a deliberate ESTIMATE, built to be
// honest about the one thing that matters: it must never claim to be finished
// before the results actually are.
//
// The curve is a QUADRATIC ease-out over a fixed ramp, driven by elapsed time.
// It replaced an exponential one (each tick closing a fixed fraction of the
// remainder), which was correct on paper but felt broken: that shape ran at
// ~30%/s at the start and ~1%/s by the end, so it sprinted to 50% in two seconds
// and then appeared to stall for the rest of the search. Quadratic spans roughly
// 15%/s down to 3%/s — still decelerating, but evenly enough to read as one
// continuous movement.
//
// Time-based, not per-tick-increment: the position is derived from the elapsed
// milliseconds every tick, so a throttled background tab or a dropped frame
// cannot make the bar drift behind where it should be.
const CEIL = 90;      // the timer's hard ceiling — only a real result passes it
const TICK = 60;      // ms between updates: smooth without thrashing React
const RAMP_MS = 11000; // time to coast from 0 to CEIL; then it waits there
// FINISHING BEAT — deliberately unhurried, and the reason is the whole point of
// having a bar at all. A search typically lands somewhere around 50–70%, so the
// run home is a THIRD of the track. Covered in a couple of hundred milliseconds
// it does not read as motion at all: the customer sees "70%", a blink, and the
// results — which is exactly the jump this replaced. Given long enough to be
// watched, the same distance reads as the search completing.
//
// This does hold back results the customer already has, so it is bounded and
// tuned rather than generous: about a second, all told.
const FINISH_MS = 700;   // sweep from wherever the ramp reached up to 100
const SWEEP_TICK = 25;   // ms; ~28 steps across the sweep, so the digits COUNT up
const HOLD_MS = 300;     // 100% on screen before the card goes

// Ease-out for the sweep: quick off the mark, settling into 100 rather than
// slamming into it. Linear arrived at full speed and stopped dead.
const easeOut = (x) => 1 - (1 - x) * (1 - x);

// Quadratic ease-out, 0 → CEIL across RAMP_MS, flat at CEIL after.
const rampAt = (elapsed) => {
  const x = Math.min(elapsed / RAMP_MS, 1);
  return CEIL * (1 - (1 - x) * (1 - x));
};

export default function FlightLoadingModal({
  open,
  onClose,
  from = '',
  to = '',
  when = '',
  pax = '',
  roundTrip = false,
  // Identifies WHICH search is running (the results page passes flightSearch.seq,
  // which is bumped on every Search press and keys the queries themselves). It is
  // what tells a genuinely new search — reset to 0 — apart from `open` merely
  // flickering false for a render on the same search, which must not reset
  // anything. Without it both looked identical from in here.
  runKey = null,
}) {
  const [tip, setTip] = useState(0);
  const [pct, setPct] = useState(0);
  // Bumped per search. Used as a React `key` on the fill and the plane so a reset
  // REMOUNTS them: a fresh node renders at 0 with nothing to transition from,
  // whereas setting width 100% → 0% on the existing node made the bar sweep
  // visibly BACKWARDS (and the plane fly right-to-left) at the start of the next
  // search — the "loader goes in reverse" glitch.
  const [run, setRun] = useState(0);
  // Mirrors `pct` so the finish can read where the ramp had got to. State would
  // be stale inside the effect that starts the sweep.
  const pctRef = useRef(0);
  // When THIS run's ramp began. Held in a ref, not recreated per effect run, so a
  // flicker resumes the same climb instead of restarting it from zero.
  const startedAtRef = useRef(0);
  const lastRunRef = useRef(null);
  const put = (v) => { pctRef.current = v; setPct(v); };

  // ---- VISIBILITY IS DERIVED DURING RENDER, NOT IN AN EFFECT ----------------
  //
  // This is the fix for "the bar reached 80%, the results appeared, and THEN the
  // last 20% played". Visibility used to be `open || outro`, with `outro` set by
  // an effect. Effects run AFTER the render commits, so the moment `open` went
  // false there was one committed frame where the card returned null: it
  // unmounted, the browser painted the results behind it, and only then did the
  // effect flip `outro` and bring the card back to finish. The flash was the
  // modal genuinely leaving the DOM and coming back.
  //
  // Setting state during render is React's documented way to adjust state when a
  // prop changes: React discards this render pass and immediately re-renders with
  // the new phase BEFORE committing anything, so the null return is never painted.
  // BOTH seeds must come from `open`, and the phase one is the subtle half. This
  // modal is mounted by the results page, which arrives with its query ALREADY
  // fetching — so on one-way and round trip the very first render has open=true.
  // Seeding phase to 'idle' and waiting for a transition meant there was no
  // transition to wait for (prevOpen seeded true as well), so the loader never
  // appeared at all for those trip types. Multi-city hid the bug: its query is
  // enabled a render later, so open really does go false → true there.
  const [phase, setPhase] = useState(open ? 'running' : 'idle');
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setPhase('running');
    else if (phase === 'running') setPhase('finishing');
  }

  useEffect(() => {
    if (phase === 'running') {
      const isNewRun = runKey !== lastRunRef.current || !startedAtRef.current;
      lastRunRef.current = runKey;
      if (isNewRun) {
        startedAtRef.current = Date.now();
        setRun((r) => r + 1);
        put(0);
      }
      const id = setInterval(() => {
        const next = rampAt(Date.now() - startedAtRef.current);
        // MONOTONIC. If `open` flickered mid-search the finish sweep will have
        // nudged the bar ahead of the ramp; recomputing from elapsed time would
        // then drag it back down. A progress bar may stall, but it may never
        // retreat — so hold the high-water mark until the ramp catches up.
        put(Math.max(pctRef.current, next));
      }, TICK);
      return () => clearInterval(id);
    }
    if (phase === 'finishing') {
      // `startPct`, not `from` — that name is taken by the origin-city prop.
      const startPct = pctRef.current;
      const startedAt = Date.now();
      const sweep = setInterval(() => {
        const x = Math.min((Date.now() - startedAt) / FINISH_MS, 1);
        put(startPct + (100 - startPct) * easeOut(x));
        if (x >= 1) clearInterval(sweep);
      }, SWEEP_TICK);
      const done = setTimeout(() => {
        setPhase('idle');
        // This run is over. Clearing the start time makes the NEXT open a new run
        // even if it somehow arrives carrying the same runKey.
        startedAtRef.current = 0;
      }, FINISH_MS + HOLD_MS);
      return () => { clearInterval(sweep); clearTimeout(done); };
    }
    return undefined;
  }, [phase, runKey]);

  useEffect(() => {
    // Keyed on `phase`, not `open`: the page must stay locked through the finish
    // too, or it unlocks (and can jump) while the card is still covering it.
    if (phase === 'idle') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const rot = setInterval(() => setTip((t) => (t + 1) % TIPS.length), 1800);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      clearInterval(rot);
    };
  }, [phase, onClose]);

  // The card covers the results for the WHOLE run, finish included.
  if (phase === 'idle') return null;

  const meta = [when, pax].filter(Boolean).join(' · ');
  const shown = Math.round(pct);

  // Portaled to <body> so it shares the ROOT stacking context with the airline /
  // traveller popovers (also portaled). Without this the modal renders inside the
  // results page's nested stacking context and its z-index can't beat the
  // popovers' — they'd stay visible on top of the loader.
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Dim + blur backdrop. /95 rather than /85: the results ARE rendered behind
          this for the whole finish (the page stops loading the moment the query
          lands, whatever the bar says), and at 85% enough of them showed through
          to read as "the results arrived before the loader finished". */}
      <div className="absolute inset-0 bg-night/95 backdrop-blur-md" />

      {/* card */}
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-neon/20 bg-panel/95 px-8 pt-7 pb-8 text-center shadow-[0_30px_80px_-20px_rgba(52,239,164,.35)]">
        {/* ambient neon glows */}
        <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-72 -translate-x-1/2 rounded-full bg-neon/20 blur-[90px]" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -right-10 h-48 w-48 rounded-full bg-sky/15 blur-[80px]" aria-hidden="true" />

        <div className="relative">
          {/* brand */}
          <NeonWordmark className="mx-auto w-[132px] h-auto text-brand drop-shadow-[0_0_18px_rgba(52,239,164,.45)]" />

          {/* route */}
          <div className="mt-6 flex items-center justify-center gap-3 font-display text-[22px] font-extrabold leading-none">
            <span>{from || 'Origin'}</span>
            {/* Direction glyph reflects the trip type: a double arrow (⇌) for a
                round trip, a single arrow (→) for one-way. */}
            {roundTrip ? (
              <svg viewBox="0 0 24 24" className="w-6 h-5 text-neon shrink-0"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M4 7h15m-4-4 4 4-4 4M20 17H5m4-4-4 4 4 4" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-6 h-5 text-neon shrink-0"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M4 12h15m-5-5 5 5-5 5" /></svg>
            )}
            <span>{to || 'Destination'}</span>
          </div>
          {meta && <p className="mt-2 text-muted text-sm">{meta}</p>}

          {/* THE RUNWAY IS THE PROGRESS BAR. The plane no longer loops on a timer
              independent of anything — it sits AT the percentage, the track fills
              solid neon behind it, and the dashes ahead of it are the distance
              still to go. One metaphor doing one job, instead of an animation
              that said nothing and a bar that repeated it. */}
          <div className="mx-auto mt-6 w-full max-w-[300px]">
            {/* The percentage no longer sits up here — it is centred below the
                runway, above the dots. */}
            <div
              className="relative h-10"
              role="progressbar"
              aria-valuenow={shown}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Searching flights"
            >
              {/* dashed track — the part not yet flown */}
              <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-[repeating-linear-gradient(90deg,rgba(148,163,184,.45)_0_8px,transparent_8px_16px)]" />
              {/* flown portion. `transition` on width + left rather than a shorter
                  TICK: the interval only has to be fast enough to feed the
                  transition, and the browser interpolates the rest on the
                  compositor — smooth without re-rendering React every frame. */}
              <div
                key={`fill-${run}`}
                className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-gradient-to-r from-neon to-mint shadow-[0_0_12px_rgba(52,239,164,.7)] transition-[width] duration-150 ease-linear"
                style={{ width: `${pct}%` }}
              />
              {/* endpoints */}
              <span className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-neon shadow-[0_0_10px_rgba(52,239,164,.8)]" />
              <span
                className={'absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full border-2 border-neon transition-colors duration-200 '
                  + (shown >= 100 ? 'bg-neon shadow-[0_0_10px_rgba(52,239,164,.8)]' : '')}
              />
              {/* plane, parked at the current percentage */}
              <div
                key={`plane-${run}`}
                className="absolute top-1/2 transition-[left] duration-150 ease-linear"
                style={{ left: `${pct}%`, transform: 'translate(-50%, -50%)' }}
              >
                {/* No bob. The plane tracks the runway dead straight — a vertical
                    wobble on something that is also a progress indicator made the
                    position look imprecise. */}
                <span className="relative flex items-center justify-center">
                  <span className="absolute h-9 w-9 rounded-full bg-neon/25 blur-md" />
                  <svg viewBox="0 0 24 24" className="relative w-6 h-6 rotate-90 text-neon drop-shadow-[0_0_8px_rgba(52,239,164,.8)]">
                    <path fill="currentColor" d="M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {/* THE PERCENTAGE READOUT — centred on the card, between the runway and
              the dots. The gaps either side are deliberate: the dots BOUNCE
              (translateY -25% of their own height), so they intrude upward into
              whatever sits above them. Splitting the old single mt-5 into mt-4 +
              mt-4 keeps the number clear of the dots at the top of that bounce
              instead of letting the two read as one blob.

              tabular-nums: proportional digits change width as the number climbs,
              which makes a centred readout twitch left and right on every tick. */}
          <p className="mt-4 text-center font-display text-[18px] font-extrabold leading-none tabular-nums text-neon">{shown}%</p>

          {/* bouncing dots */}
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-neon animate-bounce" />
            <span className="w-2.5 h-2.5 rounded-full bg-mint animate-bounce" style={{ animationDelay: '.15s' }} />
            <span className="w-2.5 h-2.5 rounded-full bg-neon animate-bounce" style={{ animationDelay: '.3s' }} />
          </div>

          {/* rotating reassurance line */}
          <p key={tip} className="mt-4 text-sm text-ink/90 animate-[fade_.4s_ease]">
            {TIPS[tip].replace(/500\+ airlines…?/, '')}
            {tip === 0 && <span className="text-neon font-semibold">500+ airlines…</span>}
          </p>
        </div>
      </div>

      {/* Local keyframes. Both plane animations are gone: `nhFly` (which looped
          `left` 0→100% independently of any real state) went when `left` became
          the progress, and `nhBob` (a vertical wobble) went because the plane
          must travel the runway in one straight line. Only the status line's
          fade is left. */}
      <style>{`
        @keyframes fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
      `}</style>
    </div>,
    document.body,
  );
}
